/**
 * Phase 4 — normalize any raw phone input into the exact WhatsApp-ready
 * Indian mobile format (91 + 10 digits), regardless of how the user typed
 * it in (bare 10-digit, with 91, or with +91). Idempotent — running it twice
 * on an already-normalized number returns the same result, so it can never
 * produce a duplicated country code like "+91+919876543210".
 *
 * Returns null when the input cannot be recognized as a valid Indian mobile
 * number (caller should validate before opening WhatsApp — spec §3).
 */
export function normalizeIndianMobile(raw) {
  if (!raw) return null;
  let digits = String(raw).replace(/\D/g, "");
  digits = digits.replace(/^0+/, "");

  // Country code entered twice (e.g. user typed "+91" over an already
  // country-coded number) — collapse the duplicate down to one "91".
  if (digits.length === 14 && digits.startsWith("9191")) {
    digits = digits.slice(2);
  }

  // Already has exactly one country code — use as-is.
  if (digits.length === 12 && digits.startsWith("91")) {
    return digits;
  }
  // Bare 10-digit mobile — add the country code.
  if (digits.length === 10) {
    return `91${digits}`;
  }
  return null;
}

/** Builds a valid wa.me link. Falls back to the generic share-sheet
 * (no destination number) when `rawPhone` is empty, but returns null when a
 * non-empty phone fails to normalize — caller should surface an error
 * instead of opening a broken chat. */
export function buildWhatsAppUrl(rawPhone, text) {
  const query = text ? `?text=${encodeURIComponent(text)}` : "";
  if (!rawPhone || !String(rawPhone).trim()) {
    return `https://wa.me/${query}`;
  }
  const number = normalizeIndianMobile(rawPhone);
  if (!number) return null;
  return `https://wa.me/${number}${query}`;
}
